import { useMe } from "@quortex/front-common"
import { Navigate, Outlet } from "@tanstack/react-location"
import { useSuspenseQuery } from "@tanstack/react-query"
import { lazy } from "react"
import ApiKeys from "src/App/settings/App/General/ApiKeys"
import { useOrganizationMember } from "src/queries/organizationMembers"

// Misc
const App = lazy(() => import("./App"))
const NotFound = lazy(() =>
  import("@quortex/front-common").then(module => ({ default: module.NotFound }))
)

// General
const General = lazy(() => import("./App/General"))
const Overview = lazy(() => import("./App/General/Overview"))
const Members = lazy(() => import("./App/General/Members"))
const Billing = lazy(() => import("./App/General/Billing"))
const Commitment = lazy(() => import("./App/General/Commitment"))
const AddApiKey = lazy(() => import("./App/General/ApiKeys/AddApiKey"))
const EditApiKey = lazy(() => import("./App/General/ApiKeys/EditApiKey"))
const SSO = lazy(() => import("./App/General/SSO"))
const SSOConfigure = lazy(() => import("./App/General/SSO/SSOConfigure"))
const ServiceAndSupport = lazy(() => import("./App/General/ServiceAndSupport"))

// Link
const Link = lazy(() => import("./App/Link"))
const LinkMembers = lazy(() => import("./App/Link/Members"))
const LinkUsage = lazy(() => import("./App/Link/Usage"))

// Play
const Play = lazy(() => import("./App/Play"))
const PlayMembers = lazy(() => import("./App/Play/Members"))
const PlayUsage = lazy(() => import("./App/Play/Usage"))

// Switch
const Switch = lazy(() => import("./App/Switch"))
const SwitchUsage = lazy(() => import("./App/Switch/Usage"))

// Ingest
const Ingest = lazy(() => import("./App/Ingest"))
const IngestUsage = lazy(() => import("./App/Ingest/Usage"))

// PowerVu
const PowerVu = lazy(() => import("./App/PowerVu/PowerVu"))
const PowerVuMembers = lazy(() => import("./App/PowerVu/Members"))

const RedirectSettingsHome = () => {
  const { data: me } = useSuspenseQuery(useMe())
  const { data: memberMe } = useSuspenseQuery(useOrganizationMember({ userUuid: me.uuid }))

  if (memberMe.isAdmin) {
    return <Navigate replace to="general" />
  }

  if (memberMe.link.role === "captain") {
    return <Navigate replace to="link" />
  }

  if (memberMe.play.role === "captain") {
    return <Navigate replace to="play" />
  }

  if (memberMe.switch.role === "captain") {
    return <Navigate replace to="switch" />
  }

  if (memberMe.powervu.role === "captain") {
    return <Navigate replace to="powervu" />
  }

  return <NotFound />
}

export const settingsRoutes = {
  path: "settings",
  element: <App />,
  children: [
    { path: "/", element: <RedirectSettingsHome /> },
    {
      path: "general",
      element: <General />,
      children: [
        { path: "/", element: <Navigate replace to="overview" /> },
        {
          path: "api_keys",
          children: [
            { path: "/", element: <ApiKeys /> },
            { path: "new", element: <AddApiKey /> },
            { path: ":apiKeyUuid", element: <EditApiKey /> },
            { element: <NotFound /> }
          ]
        },
        {
          path: "billing",
          element: <Outlet />,
          children: [{ path: "/", element: <Billing /> }, { element: <NotFound /> }]
        },
        { path: "commitment", element: <Commitment /> },
        { path: "members", element: <Members /> },

        { path: "overview", element: <Overview /> },
        {
          path: "sso",
          children: [
            { path: "/", element: <SSO /> },
            { path: "configure", element: <SSOConfigure /> },
            { element: <NotFound /> }
          ]
        },
        { path: "service", element: <ServiceAndSupport /> },
        { element: <NotFound /> }
      ]
    },
    {
      path: "link",
      element: <Link />,
      children: [
        { path: "/", element: <Navigate replace to="usage" /> },
        { path: "members", element: <LinkMembers /> },
        { path: "usage", element: <LinkUsage /> },
        { element: <NotFound /> }
      ]
    },
    {
      path: "play",
      element: <Play />,
      children: [
        { path: "/", element: <Navigate replace to="usage" /> },
        { path: "members", element: <PlayMembers /> },
        { path: "usage", element: <PlayUsage /> },
        { element: <NotFound /> }
      ]
    },
    {
      path: "switch",
      element: <Switch />,
      children: [
        { path: "/", element: <Navigate replace to="usage" /> },
        { path: "usage", element: <SwitchUsage /> },
        { element: <NotFound /> }
      ]
    },
    {
      path: "ingest",
      element: <Ingest />,
      children: [
        { path: "/", element: <Navigate replace to="usage" /> },
        { path: "usage", element: <IngestUsage /> }
      ]
    },
    {
      path: "powervu",
      element: <PowerVu />,
      children: [
        { path: "/", element: <Navigate replace to="members" /> },
        { path: "members", element: <PowerVuMembers applicationDisplayName={"PowerVu"} /> },
        { element: <NotFound /> }
      ]
    },
    { element: <NotFound /> }
  ]
}
